import { useEffect, useState } from "react";
import { BackArrow, SaveIcon } from "allIcons";
import {
  BodyContainer,
  TextEditor,
  HeaderContainer,
  IconWrapper,
  InputField,
  ListCard,
  PreviewCard,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  ListIconWrapper,
  AddComponent,
  MainButton,
  PopOver,
  AddFromTemplateLibrary,
  DropDownCard,
  Loader,
  DeleteButton,
  ToggleCard,
  SelectField,
} from "commonUI";
import { useParams } from "react-router-dom";
import { useContentRequestsItem } from "api/templateLibrary/contentRequestsItem";
import {
  contentRequestItemType,
  getUserSavePermission,
  isEmpty,
  isEqual,
  parse,
  useUtilities,
} from "helpers";
import { useTags } from "api/siteSettings/Tags";
import {
  useAddContentRequest,
  useContentRequests,
  useDeleteContentRequest,
  useUpdateContentRequest,
} from "api/templateLibrary/contentRequests";
import { updateFormState } from "redux/reducers/templateLibrary/contentRequests";
import { useUserPermissions } from "hooks/user";
import { usePhases } from "api/templateLibrary/phases";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function AddEditContentRequestTemplate({previousPath=`/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-templates`, isSiteTemplate}) {
  const {
    contentRequests: { form },
    navigate,
  } = useUtilities();
  const { id } = useParams();
  const { notification, dispatch } = useUtilities();
  const { data: contentRequestItem, isLoading: loadingContentRequestItem } =
    useContentRequestsItem();
  const { data: contentRequests, isLoading: loadingContentRequests } =
    useContentRequests();
  const { mutate: addContentRequest, isLoading: isAdding } =
    useAddContentRequest();
  const { mutate: updateContentRequest, isLoading: isUpdating } =
    useUpdateContentRequest();
  const { mutate: deleteContentRequest, isLoading: isDeleting } =
    useDeleteContentRequest(true);
  const { data: tagsList } = useTags();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();
  const { data: phases, isLoading: loadingPhases } = usePhases();

  const [inputValues, setInputValues] = useState(
    isEmpty(form)
      ? {
          title: "",
          message: "",
          contentitems: [],
          tags: [],
          phase_id: "",
          phase_due_date_auto: 0,
        }
      : form
  );
  const [openAddContentItemModal, setOpenAddContentItemModal] = useState(false);
  const [openAddTagModal, setOpenAddTagModal] = useState(false);
  
  useEffect(() => {
    if (
      id === "add" ||
      loadingContentRequests ||
      loadingContentRequestItem ||
      contentRequests.length === 0
    )
      return;
    const contentRequest = contentRequests.find((ele) => isEqual(ele.id, id));
    if (contentRequest) {
      setInputValues({
        ...contentRequest,
        contentitems: (parse(contentRequest.contentitems) ?? []).filter((_) => !!_),
        tags: parse(contentRequest.tags || []),
      });
    }
  }, [
    contentRequests,
    loadingContentRequests,
    contentRequestItem,
    id,
    loadingContentRequestItem,
  ]);
  const hanldeSave = () => {
    if (inputValues.title === "") {
      notification({
        type: "error",
        message: t("common.input.error.titleRequired"),
      });
      return;
    }
    if (id === "add" && add_new_templates) {
      addContentRequest({
        ...inputValues,
      });
      return;
    }
    if (edit_templates)
      updateContentRequest({
        ...inputValues,
        id,
      });
  };

  return (
    <>
      <RightMainContainer>
        {isAdding || isUpdating || isDeleting ? <Loader loader="block" /> : ""}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() =>
                navigate(previousPath)
              }
            />
            <PrimaryHeading className="clamp-text">
              {id === "add" ? t("libraryTemplate.contentRequests.addContentRequest") : inputValues.title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={!isSiteTemplate && id !== "add" && delete_templates}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteMessage={t("libraryTemplate.delete.message")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              onDelete={() => deleteContentRequest(id)}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={hanldeSave}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("common.input.title.label")}
            placeholder={t("common.input.title.placeholder")}
            value={inputValues.title}
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, title: value }))
            }
          />
          <TextEditor
            label={t("common.input.description.label")}
            placeholder={t("common.input.description.placeholder")}
            editorClass="mt-2"
            option={false}
            rows={5}
            value={inputValues.message}
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, message: value }))
            }
            reRender={id !== "add"}
          />
          <SelectField
            placeholder={t("common.input.associatedPhase.placeholder")}
            className="mt-3 gap-10 mb-3"
            label={t("common.input.associatedPhase.label")}
            value={inputValues.phase_id}
            setValue={(value) =>
              setInputValues({ ...inputValues, phase_id: value })
            }
          >
            {phases.map((phase) => (
              <option key={phase.id} value={phase.id}>
                {phase.title}
              </option>
            ))}
          </SelectField>
          <ToggleCard
            labelClass="mb-2"
            label={t("common.input.dueDate.automaticallyBasedOnPhase")}
            value={isEqual(inputValues.phase_due_date_auto, 1)}
            setValue={(value) =>
              setInputValues({
                ...inputValues,
                phase_due_date_auto: value ? 1 : 0,
              })
            }
            isLoading={loadingPhases}
            disabled={!inputValues.phase_id}
          />
          <div className="mt-3">
            <SecondaryHeading>{t("libraryTemplate.contentRequests.input.contentItems")}</SecondaryHeading>
            {inputValues.contentitems.map((item, index) => {
              return (
                <ListCard
                  key={index}
                  data={item.title}
                  edit={true}
                  editLink={`/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-items/${item.id}`}
                  onEditClick={() => {
                    dispatch(updateFormState({ ...inputValues, id }));
                  }}
                  crossIcon
                  showModalonCross
                  deleteTitle={t("common.remove.itemFromTemplate.title")}
                  deleteMessage={t("common.remove.itemFromTemplate.message")}
                  deleteButtonText={t("common.remove.itemFromTemplate.button")}
                  onCross={() => {
                    setInputValues((pre) => ({
                      ...pre,
                      contentitems: pre.contentitems.filter(
                        (_) => _.id !== item.id
                      ),
                    }));
                  }}
                  icon={
                    <ListIconWrapper>
                      {contentRequestItemType[item.hint || item.type]}
                    </ListIconWrapper>
                  }
                  deleteIcon={true}
                />
              );
            })}
          </div>
          <AddComponent
            title={t("libraryTemplate.contentRequests.input.addContent")}
            className="mt-3 mb-3"
            value={openAddContentItemModal}
            onClick={() => setOpenAddContentItemModal(true)}
          >
            <PopOver
              open={openAddContentItemModal}
              onClose={() => setOpenAddContentItemModal(false)}
              style={{
                width: "300px",
                top: "unset",
                left: "unset",
                right: "unset",
                bottom: "calc(100% + 15px)",
              }}
              toolTip={{ horizontal: "center", vertical: "bottom" }}
            >
              <AddFromTemplateLibrary
                heading={t("libraryTemplate.contentRequests.input.addContentFromRequestItem")}
                data={contentRequestItem
                  .filter(
                    (_) =>
                      inputValues.contentitems.findIndex(
                        (ele) => ele.id === _.id
                      ) === -1
                  )
                  .map((item) => ({
                    ...item,
                    onClick: () => {
                      setInputValues((pre) => ({
                        ...pre,
                        contentitems: [...pre.contentitems, item],
                      }));
                    },
                    icon: contentRequestItemType[item.hint || item.type],
                  }))}
                isLoading={loadingContentRequestItem}
              />
            </PopOver>
          </AddComponent>
          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {inputValues.tags.map((item, index) => (
                <PreviewCard
                  key={index}
                  color={item.color}
                  title={item.text}
                  icon={true}
                  className="m-0"
                  onCross={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      tags: pre.tags.filter((_) => _.id !== item.id),
                    }))
                  }
                />
              ))}
            </div>
          </div>
          <AddComponent
            value={openAddTagModal}
            onClick={() => setOpenAddTagModal(true)}
          >
            <PopOver
              open={openAddTagModal}
              onClose={() => setOpenAddTagModal(false)}
              style={{
                top: "unset",
                right: "unset",
                left: "unset",
                bottom: "calc(100% + 15px",
              }}
              toolTip={{ horizontal: "center", vertical: "bottom" }}
            >
              <DropDownCard
                title={t("common.input.tags.addTag")}
                listStyle
                filterSelectedItems
                list={tagsList?.map((item) => ({
                  ...item,
                  text: item.tag_title,
                  index: item.id,
                }))}
                onSelect={(value) => {
                  setInputValues((pre) => ({
                    ...pre,
                    tags: [...pre.tags, value],
                  }));
                }}
                selectedValues={inputValues.tags.map((item) => item.id)}
              />
            </PopOver>
          </AddComponent>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
